import React, {
	useEffect,
	useCallback,
	useState,
	useRef,
	useMemo,
} from 'react';
import { Modal, Spin, Typography, Button, Tooltip, message } from 'antd';
import { DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import CoreBadge from '../CoreBadge/CoreBadge.jsx';
import {
	FacebookIcon,
	FacebookShareButton,
	LinkedinIcon,
	LinkedinShareButton,
	TwitterShareButton,
	XIcon,
} from 'react-share';
import './viewRewardModal.scss';
import { useApolloClient, gql } from '@apollo/client';
import {
	addCommas,
	getRecognizerName,
} from '../../rewardsAndRecognitionHelper/utils.js';
import { downloadFromS3Signed } from 'src/_shared/services/utils.js';
import { useSelector } from 'react-redux';
import { MAPPED_BADGE_TYPE } from '../CoreBadge/rewardsConstants.js';
import { fetchModernFormQuestions } from 'src/_shared/services/utils.js';
import { GifPicker } from 'gif-picker-react';
import { Klipy } from 'gif-picker-react/providers/klipy';
import EmojiPicker from 'emoji-picker-react';
import EmployeePicker from 'src/RnRCommonComponent/EmployeePicker/index.js';
import AccountClaimPicker from 'src/RnRCommonComponent/EmployeePicker/AccountClaimPickerComponent';
import { ReactComponent as RecipientIcon } from './../../assets/svg/recipientOne.svg';
import emoteIcon from './../../assets/svg/emote.svg';
import gifIcon from './../../assets/svg/gif.svg';
import send from './../../assets/svg/send.svg';
import send_gray from './../../assets/svg/send_gray.svg';
import { CREATE_BADGE_ASSIGNMENT } from 'src/_shared/api/graphql/custom/recognition/recognitionMutations.js';
import { TextAreaWithTags } from 'src/home/presentation/dashboardPresentation/TextAreaWithTags.jsx';
import CustomGifPicker from 'src/home/presentation/dashboardPresentation/CustomGifPicker.jsx';
import { USER_ROLES } from 'src/_shared/constants/userRolesEnum.js';

const { Text } = Typography;

const klipyProvider = Klipy(process.env.REACT_APP_GIF_KLIPY_KEY, {
	contentFilter: 'high',
});

const ViewRewardModal = (props) => {
	const {
		isViewRewardModalVisible,
		handleCloseViewReward,
		rewardDetails,
		isModalLoaderVisible,
		isSharingVisible,
	} = props;
	const [isLoading, setIsLoading] = useState(false);
	const [formData, setFormData] = useState(null);
	const [showSendView, setShowSendView] = useState(false);
	const [recipients, setRecipients] = useState([]);
	const [selectedEmployee, setSelectedEmployee] = useState(null);
	const [selectedAccountClaimOptions, setSelectedAccountClaimOptions] =
		useState([]);
	const [fullAccountClaims, setFullAccountClaims] = useState([]);
	const [sendRewardObject, setSendRewardObject] = useState({
		content: {
			message: '',
			gifURL: null,
			tags: [],
		},
	});
	const [isGifPickerVisible, setIsGifPickerVisible] = useState(false);
	const [isEmojiPickerVisible, setIsEmojiPickerVisible] = useState(false);
	const [cursorPosition, setCursorPosition] = useState(0);
	const [isSending, setIsSending] = useState(false);
	const [shouldFocusTextArea, setShouldFocusTextArea] = useState(false);
	const client = useApolloClient();
	const currentUser = useSelector((state) => state.user.currentUser);
	const sendToPeerText =
		currentUser?.company?.recognitionSettings?.customSendToPeerText ||
		'Send to Peer';
	const hideRewardModalSendButton =
		currentUser?.featureFlags?.includes('hideRewardModalSendButton') || false;
	const rewardsEarnedMechanism =
		currentUser?.company?.recognitionSettings?.rewardsEarnedMechanism;
	const showSnappy = rewardsEarnedMechanism === 'recognition';
	const badgeType = rewardDetails?.badgeType;
	const isAssignmentRestricted = rewardDetails?.isAssignmentRestricted;
	const badgeId = rewardDetails?.badge?.id || rewardDetails?.id;
	const socialShareCompanyId =
		rewardDetails?.badge?.companyId || rewardDetails?.companyId;
	const socialShareImageCandidate = useMemo(() => {
		const defaultUrl =
			badgeId && socialShareCompanyId
				? `https://erin-images.s3.us-east-2.amazonaws.com/badges/${socialShareCompanyId}/previews/${badgeId}.png`
				: '';
		const customShareImages =
			rewardDetails?.badge?.customShareImages ||
			rewardDetails?.customShareImages;
		if (!customShareImages || customShareImages.length === 0) {
			return defaultUrl;
		}

		for (const entry of customShareImages) {
			if (entry.field && entry.prefix) {
				const fieldValue = currentUser?.accountClaim?.[entry.field];
				if (
					typeof fieldValue === 'string' &&
					fieldValue.startsWith(entry.prefix)
				) {
					return entry.url || defaultUrl;
				}
			} else if (!entry.field && !entry.prefix) {
				return entry.url || defaultUrl;
			}
		}

		return defaultUrl;
	}, [badgeId, socialShareCompanyId, rewardDetails, currentUser?.accountClaim]);
	const [socialShareUrl, setSocialShareUrl] = useState('');
	useEffect(() => {
		if (!socialShareImageCandidate) {
			setSocialShareUrl('');
			return;
		}

		const img = new Image();
		img.onload = () => setSocialShareUrl(socialShareImageCandidate);
		img.onerror = () => setSocialShareUrl('');
		img.src = socialShareImageCandidate;
	}, [socialShareImageCandidate]);
	const recognizerName = getRecognizerName(
		rewardDetails?.awardedBy,
		rewardDetails?.awardedByUser,
		currentUser?.company?.name
	);

	const socialShareText = (() => {
		const parts = [
			rewardDetails?.name
				? `I received the "${rewardDetails.name}" Recognition!`
				: 'I received a Recognition!',
		];
		if (recognizerName) parts.push(`Recognized by: ${recognizerName}`);
		if (rewardDetails?.content?.message) {
			parts.push(`They said: "${rewardDetails.content.message}"`);
		}
		return parts.join('\n\n');
	})();
	const canSendRestrictedBadge = [
		USER_ROLES.MANAGER,
		USER_ROLES.ADMIN,
	].includes(currentUser?.role);
	const isBadgeAssignmentRestricted = Boolean(
		isAssignmentRestricted || rewardDetails?.badge?.isAssignmentRestricted
	);
	const isBlockedByRestriction =
		badgeType === 'PEERTOPEER' &&
		isBadgeAssignmentRestricted &&
		!canSendRestrictedBadge;
	const canSendBadge = badgeType === 'PEERTOPEER' && !isBlockedByRestriction;
	const cannotSendBadgeMessage = isBlockedByRestriction
		? 'This Recognition can only be sent by managers.'
		: 'This Recognition is not eligible for sending.';
	const allowRecognitionsForAccountClaims =
		currentUser?.company?.recognitionSettings
			?.allowRecognitionsForAccountClaims || false;
	const requireBadgeAssignmentMessage =
		currentUser?.company?.recognitionSettings?.requireBadgeAssignmentMessage ||
		false;
	const gifIds = currentUser?.company?.gifIds;
	const prevRecipientsLengthRef = useRef(recipients.length);
	const availableTags =
		currentUser?.company?.recognitionSettings?.recognitionHashtags || [];

	useEffect(() => {
		if (!isLoading && !formData) {
			const awaitFormData = async () => {
				try {
					setIsLoading(true);
					const awaitedFormData =
						(await fetchModernFormQuestions(
							currentUser,
							'custom',
							'manager_justification_form',
							client
						)) || {};
					setFormData(awaitedFormData);
				} catch (e) {
					throw e;
				} finally {
					setIsLoading(false);
				}
			};
			awaitFormData();
		}
	}, [rewardDetails?.companyId]);

	const getJustificationContent = useCallback(
		(justificationData = null) => {
			if (justificationData === null) return [];
			const parsedJustificationData = JSON.parse(justificationData);
			const content = [];
			const validElements = ['input', 'textarea', 'select'];
			formData?.elements?.forEach((ele) => {
				if (validElements.includes(ele?.type)) {
					const answer = parsedJustificationData?.[ele.id] || '';
					content.push({ question: ele?.label, answer });
				}
			});
			return content;
		},
		[formData]
	);

	const selectedJustification = getJustificationContent(
		rewardDetails?.justificationFormAnswers
	);

	const handleOnGifClicked = (img) => {
		setSendRewardObject({
			content: {
				message: sendRewardObject?.content?.message || '',
				gifURL: img.imageUrl,
				tags: sendRewardObject?.content?.tags || [],
			},
		});
		setIsGifPickerVisible(false);
	};

	const handleOnEmojiClicked = (emoji) => {
		const newCursorPosition = cursorPosition + emoji.emoji.length;
		const messageText = sendRewardObject?.content?.message || '';
		const newText =
			messageText === ''
				? emoji.emoji
				: messageText.substring(0, cursorPosition) +
					emoji.emoji +
					messageText.substring(cursorPosition);
		setSendRewardObject({
			content: {
				message: newText,
				gifURL: sendRewardObject?.content?.gifURL || null,
				tags: sendRewardObject?.content?.tags || [],
			},
		});
		setCursorPosition(newCursorPosition);
		setShouldFocusTextArea(true);
		setIsEmojiPickerVisible(false);
	};

	const handleDeleteGif = () => {
		setSendRewardObject({
			content: {
				message: sendRewardObject?.content?.message || '',
				gifURL: null,
				tags: sendRewardObject?.content?.tags || [],
			},
		});
	};

	useEffect(() => {
		const asyncCheckEligibility = async () => {
			const currentRecipientsLength = recipients.length;
			const prevRecipientsLength = prevRecipientsLengthRef.current;

			if (currentRecipientsLength > prevRecipientsLength) {
				const newRecipient = recipients[recipients.length - 1];
				await checkAssignmentEligibility({
					userId: newRecipient.userId,
					accountClaimId: newRecipient.accountClaimId,
					badgeId: badgeId,
					recipients: recipients,
					selectedAccountClaimOptions: selectedAccountClaimOptions,
					fullAccountClaims: fullAccountClaims,
				});
			}

			prevRecipientsLengthRef.current = currentRecipientsLength;
		};
		asyncCheckEligibility();
	}, [
		recipients.length,
		recipients,
		selectedAccountClaimOptions,
		fullAccountClaims,
	]);

	const GET_BADGE_ASSIGNMENT_ELIGIBILITY = gql`
		query GetBadgeAssignmentEligibility(
			$userId: ID
			$accountClaimId: ID
			$badgeId: ID!
		) {
			getBadgeAssignmentEligibility(
				userId: $userId
				accountClaimId: $accountClaimId
				badgeId: $badgeId
			)
		}
	`;

	const checkAssignmentEligibility = async ({
		userId,
		accountClaimId,
		badgeId,
		recipients,
		selectedAccountClaimOptions,
		fullAccountClaims,
	}) => {
		const variables = {
			badgeId: badgeId,
		};
		if (userId) {
			variables.userId = userId;
		}
		if (accountClaimId) {
			variables.accountClaimId = accountClaimId;
		}
		const { data } = await client.query({
			query: GET_BADGE_ASSIGNMENT_ELIGIBILITY,
			variables: variables,
			fetchPolicy: 'network-only',
		});
		const isEligible = data?.getBadgeAssignmentEligibility || false;
		if (!isEligible) {
			const newRecipients = [...recipients];
			const newSelectedAccountClaimOptions = [...selectedAccountClaimOptions];
			const newFullAccountClaims = [...fullAccountClaims];
			newRecipients.pop();
			newSelectedAccountClaimOptions.pop();
			newFullAccountClaims.pop();
			setRecipients(newRecipients);
			setSelectedAccountClaimOptions(newSelectedAccountClaimOptions);
			setFullAccountClaims(newFullAccountClaims);
			setSelectedEmployee(newRecipients[0]?.userId || null);
			message.info(
				'This person is ineligible to recieve the selected Recognition.'
			);
		}
		return isEligible;
	};

	const handleSendBadge = async () => {
		try {
			setIsSending(true);

			const isNotPreApproved = !rewardDetails?.isPreApproved;
			const requireAdminApproval =
				currentUser?.company?.recognitionSettings?.requireAdminApproval;

			const messageText = sendRewardObject?.content?.message || '';

			const variables = {
				input: {
					accountClaimId: recipients[0]?.accountClaimId || null,
					badgeId: badgeId,
					companyId:
						rewardDetails?.badge?.companyId || rewardDetails?.companyId,
					userId: recipients[0]?.userId || 'account_claim',
					awardedBy: currentUser?.id,
					managerAssigned: false,
					points: rewardDetails?.points,
					rewardBonus: rewardDetails?.rewardBonus,
					recipients: recipients,
					approvalStatus:
						isNotPreApproved || requireAdminApproval ? 'Pending' : 'Approved',
					content: {
						message: messageText.trim(),
						gifURL: sendRewardObject?.content?.gifURL || null,
						tags: sendRewardObject?.content?.tags || [],
					},
				},
			};

			await client.mutate({
				mutation: CREATE_BADGE_ASSIGNMENT,
				variables: variables,
			});

			message.success(
				isNotPreApproved || requireAdminApproval
					? 'Recognition Successfully Sent for Approval'
					: 'Recognition Successfully Sent'
			);

			setShowSendView(false);
			setRecipients([]);
			setSelectedEmployee(null);
			setSelectedAccountClaimOptions([]);
			setFullAccountClaims([]);
			setSendRewardObject({ content: { message: '', gifURL: null, tags: [] } });
			setIsGifPickerVisible(false);
			setIsEmojiPickerVisible(false);

			handleCloseViewReward();
		} catch (error) {
			console.error('Error sending Recognition:', error);
			message.error('Failed to send Recognition. Please try again.');
		} finally {
			setIsSending(false);
		}
	};

	const handleDownloadCertificate = async () => {
		try {
			const signedUrl = await downloadFromS3Signed(
				rewardDetails.certificateKey,
				'erin-documents'
			);
			if (!signedUrl) {
				message.error('Failed to download certificate.');
				return;
			}

			const response = await fetch(signedUrl);
			if (!response.ok) {
				message.error('Failed to download certificate.');
				return;
			}

			const blob = await response.blob();
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = 'certificate.pdf';
			document.body.append(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Error downloading certificate:', error);
			message.error('Failed to download certificate.');
		}
	};

	const isEmployeeDetailValid =
		(allowRecognitionsForAccountClaims &&
			selectedAccountClaimOptions?.length > 0) ||
		(!allowRecognitionsForAccountClaims && recipients?.length > 0);

	const isContentValid = (() => {
		const messageText = sendRewardObject?.content?.message || '';
		const hasMessage = messageText && messageText.trim().length !== 0;
		const hasGif = sendRewardObject?.content?.gifURL;

		if (requireBadgeAssignmentMessage) {
			return hasMessage;
		}

		return hasMessage || hasGif;
	})();

	const earnedDate =
		rewardDetails?.earnedDate ||
		rewardDetails?.completedAt ||
		rewardDetails?.updatedAt ||
		rewardDetails?.createdAt ||
		null;

	return (
		<>
			<Modal
				open={isViewRewardModalVisible}
				onCancel={() => {
					setShowSendView(false);
					setRecipients([]);
					setSelectedEmployee(null);
					setSelectedAccountClaimOptions([]);
					setFullAccountClaims([]);
					setSendRewardObject({
						content: { message: '', gifURL: null, tags: [] },
					});
					setIsGifPickerVisible(false);
					setIsEmojiPickerVisible(false);
					handleCloseViewReward();
				}}
				footer={false}
				width={600}
				destroyOnClose
			>
				{isModalLoaderVisible ? (
					<div className="d-flex align-items-center justify-content-center w-100 h-100">
						{' '}
						<Spin />{' '}
					</div>
				) : showSendView ? (
					<div className="physical-reward-modal">
						<div className="row w-100">
							<div className="col-12">
								<h4 className="physical-reward-text mb-3">
									Send Recognition to Peer
								</h4>
								<div className="pb-2 w-100">
									<div
										className="send-modal-container"
										style={{ marginBottom: '16px' }}
									>
										<div
											style={{
												borderRadius: 24,
												color: 'var(--primary-btn-color, var(--theme-primary))',
												fontSize: '14px',
												fontWeight: 700,
												border: 'none',
											}}
										>
											{allowRecognitionsForAccountClaims ? (
												<AccountClaimPicker
													value={selectedAccountClaimOptions}
													onChange={(newAcc) => {
														setSelectedAccountClaimOptions(newAcc);
													}}
													companyId={currentUser?.companyId}
													fullValue={fullAccountClaims}
													setFullValue={(selectionArray) => {
														const mappedRecipients = selectionArray.map(
															(selection) => {
																const recipient = {
																	accountClaimId: selection.claim.id,
																};
																if (selection.claim?.userId) {
																	recipient.userId = selection.claim.userId;
																}
																return recipient;
															}
														);
														setRecipients(mappedRecipients);
														setFullAccountClaims(selectionArray);
													}}
													style={{
														borderRadius: 24,
														color:
															'var(--primary-btn-color, var(--theme-primary))',
														fontSize: '14px',
														fontWeight: 700,
														border:
															'1px solid var(--primary-btn-color, var(--theme-primary))',
														width: '158px',
														background: 'transparent',
													}}
												/>
											) : (
												<EmployeePicker
													style={{
														borderRadius: 24,
														color:
															'var(--primary-btn-color, var(--theme-primary))',
														fontSize: '14px',
														fontWeight: 700,
														border:
															'1px solid var(--primary-btn-color, var(--theme-primary))',
														width: '158px',
														background: 'transparent',
														height: '34px',
													}}
													allowMultiple={true}
													selectedEmployee={selectedEmployee}
													setSelectedEmployee={(value) => {
														setSelectedEmployee(value);
													}}
													recipients={recipients}
													setRecipients={setRecipients}
													placeholder={
														<div style={{ width: 120 }}>
															<RecipientIcon
																style={{
																	height: 18,
																	width: 18,
																	marginRight: '4px',
																	display: 'inline-block',
																}}
															/>
															<span style={{ verticalAlign: 'middle' }}>
																Select Recipients
															</span>
														</div>
													}
												/>
											)}
										</div>
									</div>
								</div>
								<div className="mb-2 text-area-gif">
									<TextAreaWithTags
										sendRewardObject={sendRewardObject}
										setSendRewardObject={setSendRewardObject}
										availableTags={availableTags}
										cursorPosition={cursorPosition}
										setCursorPosition={setCursorPosition}
										shouldFocusTextArea={shouldFocusTextArea}
										setShouldFocusTextArea={setShouldFocusTextArea}
									/>
									{sendRewardObject?.content?.gifURL && (
										<div className="selected-gif">
											<img
												src={sendRewardObject.content.gifURL}
												alt="Selected GIF"
											/>
											<div className="gif-close-btn">
												<DeleteOutlined onClick={handleDeleteGif} />
											</div>
										</div>
									)}
								</div>
								<div className="d-flex align-items-center w-100 justify-content-between">
									<div className="d-flex">
										<div
											className="pr-2 gif-emogi"
											onClick={() => {
												setIsEmojiPickerVisible(false);
												setIsGifPickerVisible(!isGifPickerVisible);
											}}
										>
											<img src={gifIcon} alt="gifIcon" />
										</div>
										<div
											className="gif-emogi"
											onClick={(e) => {
												e.preventDefault();
												setIsGifPickerVisible(false);
												setIsEmojiPickerVisible(!isEmojiPickerVisible);
											}}
										>
											<img src={emoteIcon} alt="emoteIcon" />
										</div>
									</div>
									<div>
										{isSending ? (
											<Spin className="d-flex align-items-center justify-content-center w-100 h-100" />
										) : (
											<Tooltip
												title={
													requireBadgeAssignmentMessage &&
													isEmployeeDetailValid &&
													(!sendRewardObject?.content?.message ||
														sendRewardObject.content.message.trim().length ===
															0)
														? 'A text message is required for badge assignments.'
														: !isEmployeeDetailValid
															? 'Please select a recipient.'
															: ''
												}
												placement="topRight"
												open={
													(requireBadgeAssignmentMessage &&
														isEmployeeDetailValid &&
														(!sendRewardObject?.content?.message ||
															sendRewardObject.content.message.trim().length ===
																0)) ||
													!isEmployeeDetailValid
														? undefined
														: false
												}
											>
												<div className="send-primary-btn">
													<Button
														type="primary"
														onClick={handleSendBadge}
														disabled={
															!(
																isEmployeeDetailValid &&
																(requireBadgeAssignmentMessage
																	? sendRewardObject?.content?.message &&
																		sendRewardObject.content.message.trim()
																			.length > 0
																	: isContentValid)
															)
														}
													>
														<span className="d-flex align-items-center">
															<span className="pr-2">Send </span>
															{!(
																isEmployeeDetailValid &&
																(requireBadgeAssignmentMessage
																	? sendRewardObject?.content?.message &&
																		sendRewardObject.content.message.trim()
																			.length > 0
																	: isContentValid)
															) ? (
																<img
																	src={send_gray}
																	style={{ height: 18, width: 18 }}
																/>
															) : (
																<img
																	src={send}
																	style={{ height: 18, width: 18 }}
																/>
															)}
														</span>
													</Button>
												</div>
											</Tooltip>
										)}
									</div>
								</div>
								<div className="gif-emogi-modal mt-2">
									{isGifPickerVisible &&
										(gifIds ? (
											<CustomGifPicker
												gifIds={gifIds}
												onGifClick={handleOnGifClicked}
											/>
										) : (
											<GifPicker
												provider={klipyProvider}
												onGifClick={handleOnGifClicked}
											/>
										))}
									<EmojiPicker
										open={isEmojiPickerVisible}
										lazyLoadEmojis={true}
										hiddenEmojis={[
											'1f922',
											'1f92e',
											'1f621',
											'1f92c',
											'1f480',
											'2620-fe0f',
											'1f4a9',
											'1f4a3',
											'1f595',
											'1f44e',
											'1f44a',
											'1f6ac',
											'26b0-fe0f',
											'1faa6',
											'26b1-fe0f',
											'1f52b',
										]}
										onEmojiClick={handleOnEmojiClicked}
									/>
								</div>
							</div>
						</div>
					</div>
				) : (
					rewardDetails && (
						<div className="physical-reward-modal">
							<div className="row w-100">
								<div className="col-sm-5 col-12">
									<div
										style={{
											height: isSharingVisible ? 'auto' : '100%',
										}}
										className="physical-reward-badge"
									>
										{rewardDetails?.badgeType ? (
											<CoreBadge
												badgeType={rewardDetails?.badgeType}
												badgeIcon={rewardDetails?.iconInfo?.code}
												label={rewardDetails?.label}
												color={rewardDetails?.color}
												gradientColor={rewardDetails?.gradientColor}
												shape={rewardDetails?.shape}
												badgeRotation={rewardDetails?.badgeRotation}
												customImageSignedUrl={
													rewardDetails?.customImageSignedUrl
												}
												labelColor={rewardDetails?.labelColor}
												customIconSignedUrl={rewardDetails?.customIconSignedUrl}
											/>
										) : (
											<></>
										)}
									</div>
									{isSharingVisible ? (
										<>
											<div className="d-flex justify-content-center">
												<p
													className="physical-reward-details-semi-title"
													style={{
														marginBottom: '12px',
														marginTop: 0,
														textAlign: 'center',
													}}
												>
													Tell Others About Your Recognition
												</p>
											</div>

											<div className="physical-social-icon d-flex align-items-center justify-content-center">
												<div>
													<button
														type="button"
														style={{
															cursor: 'pointer',
															textAlign: 'center',
															marginRight: '6px',
															background: 'none',
															border: 'none',
															padding: 0,
														}}
														onClick={() => {
															const text =
																socialShareText + '\n\n' + socialShareUrl;
															const url = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}`;
															const w = 600;
															const h = 600;
															const left =
																window.screenX + (window.outerWidth - w) / 2;
															const top =
																window.screenY + (window.outerHeight - h) / 2;
															window.open(
																url,
																'linkedin-share',
																`width=${w},height=${h},left=${left},top=${top},noopener,noreferrer`
															);
														}}
													>
														<LinkedinIcon round alt="LinkedIn" size={32} />
													</button>
												</div>
												<div className="">
													<FacebookShareButton
														style={{
															cursor: 'pointer',
															textAlign: 'center',
															marginRight: '6px',
														}}
														url={socialShareUrl}
														quote={socialShareText}
													>
														<FacebookIcon round alt="Facebook" size={32} />
													</FacebookShareButton>
												</div>
												<div className="">
													<TwitterShareButton
														style={{ cursor: 'pointer', textAlign: 'center' }}
														url={socialShareUrl}
														title={socialShareText}
													>
														<XIcon round alt="X" size={32} />
													</TwitterShareButton>
												</div>
											</div>
										</>
									) : null}
								</div>
								<div className="col-sm-7 col-12">
									<div className="physical-reward-details">
										<h4 className="physical-reward-text">
											<>
												{rewardDetails?.badgeTypeCode ===
												MAPPED_BADGE_TYPE.AUTOMATED
													? `${rewardDetails?.companyName} `
													: ''}
											</>
											{rewardDetails?.name}
										</h4>
										<p className="physical-reward-details line-clamp-three">
											{rewardDetails?.description}
										</p>
										<div className="award-detalis">
											<div
												className="award-detalis-info"
												style={{
													justifyContent: 'center',
													marginBottom: 24,
													flexDirection: 'column',
													textAlign: 'center',
												}}
											>
												<div className="award-detalis-about pr-4">
													<span className="award-detalis-name pr-2">
														Earned Date:
													</span>
													<span className="award-detalis-numbers">
														{earnedDate
															? new Date(earnedDate).toLocaleDateString(
																	'en-US',
																	{
																		month: '2-digit',
																		day: '2-digit',
																		year: 'numeric',
																	}
																)
															: 'N/A'}
													</span>
												</div>
												{recognizerName ? (
													<div className="award-detalis-about pr-4">
														<span className="award-detalis-name pr-2">
															Recognized by:
														</span>
														<span className="award-detalis-numbers">
															{' '}
															{recognizerName}
														</span>
													</div>
												) : null}
												{rewardDetails?.content?.message && (
													<div
														style={{
															padding: '12px',
														}}
													>
														<Text
															style={{
																fontSize: '16px',
																lineHeight: '1.6',
																fontStyle: 'italic',
															}}
														>
															"{rewardDetails.content.message}"
														</Text>
													</div>
												)}
												{rewardDetails?.snappyGrantId ||
												rewardDetails?.points ? (
													<div className="award-detalis-about">
														<span className="award-detalis-name pr-2">
															{rewardDetails?.snappyGrant && showSnappy
																? 'Reward'
																: 'Points'}
															:
														</span>
														<span className="award-detalis-numbers">
															{rewardDetails?.snappyGrant?.name
																? rewardDetails.snappyGrant.name
																: addCommas(rewardDetails?.points || 0)}
														</span>
													</div>
												) : null}
											</div>
											<div style={{ padding: '0 12px', fontSize: '16px' }}>
												{selectedJustification &&
												Array.isArray(selectedJustification)
													? selectedJustification.map((j, i) => (
															<div key={i}>
																<span style={{ color: 'rgba(0, 0, 0, 0.85)' }}>
																	<strong>
																		{j?.question
																			? j.question + ':'
																			: 'Question:'}
																	</strong>
																</span>
																<p style={{ color: 'rgba(0, 0, 0, 0.75)' }}>
																	{j?.answer || 'No answer provided'}
																</p>
															</div>
														))
													: null}
											</div>
											{rewardDetails?.isBadgeAssignment ? null : (
												<p className="text-gray line-clamp-three">
													{showSnappy
														? rewardDetails?.snappyGrant?.description
														: `Only ${rewardDetails?.issuedBadgePercentage || 0}% employess have earned this recognition!`}
												</p>
											)}
										</div>
										{!hideRewardModalSendButton && (
											<Tooltip
												title={canSendBadge ? null : cannotSendBadgeMessage}
											>
												<div className="d-flex justify-content-center mt-3">
													<Button
														type="primary"
														onClick={() => setShowSendView(true)}
														disabled={!canSendBadge}
													>
														{sendToPeerText}
													</Button>
												</div>
											</Tooltip>
										)}
										{rewardDetails?.certificateKey && (
											<div className="d-flex justify-content-center mt-3">
												<Button
													icon={<DownloadOutlined />}
													onClick={handleDownloadCertificate}
												>
													Download Certificate
												</Button>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
					)
				)}
			</Modal>
		</>
	);
};

export default ViewRewardModal;
